import React, { useState, useEffect, FC, useCallback, useRef } from "react";
import { IconButton, Divider, Box, Typography, Button } from "@material-ui/core";
import CancelIcon from "@material-ui/icons/Cancel";
import ApiService from "../services/ApiService";
import { useAsyncAction } from "legacy-components/form/action/AsyncAction";
import NoAuthForm from "./NoAuthForm";
import { Alert } from "@material-ui/lab";
import { resolveAPIErrorMessage } from "modus/components/error-message/ErrorMessage";
import { from } from "rxjs";
import { ResourceAuthorizer } from "../../authorizer/components/Authorizer";

export interface NoAuthConfigProps {
  deployedUrl?: string;
  deploymentId: string;
  noAuthResponse?: INoAuthResponse;
  refreshDeployments: () => void;
  teamId?: string;
  deploymentStatus?: string;
  serviceId: string;
}

export interface INoAuthMethod {
  method: string;
  noAuthId: string;
}

export interface INoAuthPathDetail {
  path: string;
  methods: INoAuthMethod[];
}

export interface INoAuthResponse {
  pathVerbDetails: INoAuthPathDetail[];
}

interface IPendingAddition {
  requestVerb: string;
  requestPath: string;
}

const NoAuthConfig: FC<NoAuthConfigProps> = ({
  deployedUrl,
  deploymentId,
  noAuthResponse,
  refreshDeployments,
  deploymentStatus,
  serviceId,
}) => {
  const [noAuth, setNoAuth] = useState<INoAuthResponse>();
  const [pendingAdditions, setPendingAdditions] = useState<IPendingAddition[]>([]);
  const [pendingDeletionIds, setPendingDeletionIds] = useState<string[]>([]);

  const pendingRef = useRef({ additions: pendingAdditions, deletionIds: pendingDeletionIds });
  pendingRef.current = { additions: pendingAdditions, deletionIds: pendingDeletionIds };

  const init = useCallback(() => {
    setNoAuth(noAuthResponse);
  }, [noAuthResponse]);

  useEffect(() => {
    init();
  }, [init]);

  const onAddPath = useCallback((path: string, method: string) => {
    setPendingAdditions((prev) => [...prev, { requestVerb: method, requestPath: path }]);
  }, []);

  const onMarkForDeletion = (noAuthId: string) => {
    setPendingDeletionIds((prev) => [...prev, noAuthId]);
  };

  const onRemovePendingAddition = (index: number) => {
    setPendingAdditions((prev) => prev.filter((_, i) => i !== index));
  };

  const [submitState, { subscribe: triggerSubmit }] = useAsyncAction(() => {
    const { additions, deletionIds } = pendingRef.current;
    const tasks: Promise<any>[] = [];

    if (additions.length > 0) {
      tasks.push(
        ApiService.createNoAuthConfiguration(deploymentId, additions).toPromise()
      );
    }
    deletionIds.forEach((id) => {
      tasks.push(
        ApiService.deleteNoAuthConfiguration(deploymentId, id).toPromise()
      );
    });

    return from(Promise.all(tasks));
  });

  useEffect(() => {
    if (submitState?.loaded && !submitState?.error) {
      setPendingAdditions([]);
      setPendingDeletionIds([]);
      refreshDeployments();
    }
  }, [submitState?.loaded, submitState?.error, refreshDeployments]);

  const hasPendingChanges = pendingAdditions.length > 0 || pendingDeletionIds.length > 0;
  const isDeploying =
    deploymentStatus === "DEPLOYING" || deploymentStatus === "DEPLOY_INITIATED";

  const visibleExistingItems =
    noAuth?.pathVerbDetails
      .map((pathDetail) => ({
        ...pathDetail,
        methods: pathDetail.methods.filter(
          (m) => !pendingDeletionIds.includes(m.noAuthId)
        ),
      }))
      .filter((pathDetail) => pathDetail.methods.length > 0) ?? [];

  const pendingByPath = pendingAdditions.reduce<Record<string, string[]>>(
    (acc, item) => {
      acc[item.requestPath] = acc[item.requestPath] ?? [];
      acc[item.requestPath].push(item.requestVerb);
      return acc;
    },
    {}
  );

  return (
    <>
      <Box style={{ padding: "20px" }}>
        <Typography variant="body1" style={{ fontWeight: 600 }}>
          No Auth Configuration
        </Typography>
        <NoAuthForm
          deployedUrl={deployedUrl}
          deploymentId={deploymentId}
          deploymentStatus={deploymentStatus}
          serviceId={serviceId}
          onAddPath={onAddPath}
        />

        {visibleExistingItems.map((pathVerbDetail) => (
          <div key={pathVerbDetail.path} className="noauthdata">
            <div className="endpoint">{pathVerbDetail.path}</div>
            {pathVerbDetail.methods.map((method) => (
              <React.Fragment key={method.noAuthId}>
                <div className="verb">{method.method}</div>
                <div className="cancel">
                  <IconButton
                    size="small"
                    aria-label="close"
                    style={{ color: "#A9A9A9" }}
                    disabled={isDeploying}
                    onClick={() => onMarkForDeletion(method.noAuthId)}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </div>
              </React.Fragment>
            ))}
            <div className="divider">
              <Divider />
            </div>
          </div>
        ))}

        {Object.entries(pendingByPath).map(([path, methods]) => (
          <div key={`pending-${path}`} className="noauthdata">
            <div className="endpoint" style={{ color: "#0063a3" }}>
              {path}
            </div>
            {methods.map((method, idx) => {
              const additionIndex = pendingAdditions.findIndex(
                (a) => a.requestPath === path && a.requestVerb === method
              );
              return (
                <React.Fragment key={`${method}-${idx}`}>
                  <div className="verb" style={{ color: "#0063a3" }}>
                    {method}
                  </div>
                  <div className="cancel">
                    <IconButton
                      size="small"
                      aria-label="close"
                      style={{ color: "#A9A9A9" }}
                      onClick={() => onRemovePendingAddition(additionIndex)}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </div>
                </React.Fragment>
              );
            })}
            <div className="divider">
              <Divider />
            </div>
          </div>
        ))}

        {submitState?.error && (
          <Alert severity="error">{resolveAPIErrorMessage(submitState?.error)}</Alert>
        )}

        {hasPendingChanges && (
          <Box style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
            <ResourceAuthorizer
              action={"update"}
              resourceType={"SERVICE"}
              resourceId={serviceId}
              hideErrorMessage
            >
              <Button
                variant="contained"
                color="primary"
                onClick={() => triggerSubmit()}
                disabled={isDeploying || submitState?.loading}
                style={{
                  textTransform: "none",
                  borderRadius: "4px",
                  padding: "8px 24px",
                  backgroundColor: isDeploying || submitState?.loading ? "#00000042" : "#0063a3",
                }}
              >
                Submit
              </Button>
            </ResourceAuthorizer>
          </Box>
        )}
      </Box>
    </>
  );
};

export default NoAuthConfig;
